// frontend/client/src/components/AppSettings.jsx
// FIXED: Removed duplicate 'border' key inside Section Tabs map (eslint: no-dupe-keys)
// FIXED: /api/settings (404) replaced with /api/users/profile (settings subdoc)
// All save/load operations now use the correct endpoint.

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';
import { api } from '../services/apiClient';
import {
  Moon, Sun, Volume2, VolumeX, Bell, BellOff, Shield,
  CreditCard, Trash2, LogOut, ChevronRight, Globe, Clock,
  Smartphone, AlertTriangle, Check, Loader2, Download,
  Eye, EyeOff, Lock, KeyRound, Mail
} from 'lucide-react';

const TOAST_CSS = `
  .toast-enter { animation: toastIn 0.3s ease forwards; }
  @keyframes toastIn {
    from { opacity: 0; transform: translateY(-8px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .settings-row {
    border: 1.5px solid var(--border-light);
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 10px;
    background: var(--bg-surface);
    transition: all 0.2s ease;
    cursor: pointer;
  }
  .settings-row:hover {
    border-color: var(--brand-primary);
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.08);
  }
  .settings-row:active { transform: scale(0.995); }
  .settings-row .row-label {
    font-size: 13px; font-weight: 700;
    color: var(--text-main); margin: 0 0 3px;
  }
  .settings-row .row-sub {
    font-size: 11px; color: var(--text-muted);
    margin: 0; line-height: 1.4;
  }
  .settings-row.active {
    border-color: var(--brand-primary);
    background: var(--brand-secondary);
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.12);
  }
  .settings-row.active .row-label { color: var(--brand-primary); }
  .settings-row.active .row-sub   { color: var(--brand-primary); opacity: 0.8; }
  .toggle-track {
    width: 44px; height: 24px; border-radius: 12px;
    border: none; position: relative; cursor: pointer;
    flex-shrink: 0; transition: background 0.25s ease; outline: none;
  }
  .toggle-track:focus-visible { box-shadow: 0 0 0 2px var(--brand-primary); }
  .toggle-knob {
    width: 18px; height: 18px; border-radius: 50%;
    background: white; position: absolute; top: 3px;
    transition: left 0.25s cubic-bezier(0.16,1,0.3,1);
    box-shadow: 0 1px 3px rgba(0,0,0,0.15);
  }
  .toggle-track.on  { background: var(--brand-primary); box-shadow: 0 2px 8px rgba(79,70,229,0.3); }
  .toggle-track.on  .toggle-knob { left: 23px; }
  .toggle-track.off { background: var(--border-medium); box-shadow: inset 0 1px 3px rgba(0,0,0,0.1); }
  .toggle-track.off .toggle-knob { left: 3px; }
  .select-wrapper {
    border: 1.5px solid var(--border-light); border-radius: 10px;
    padding: 10px 12px; background: var(--bg-surface);
    transition: all 0.2s ease; cursor: pointer;
  }
  .select-wrapper:hover, .select-wrapper:focus-within {
    border-color: var(--brand-primary);
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.08);
  }
  .select-wrapper select {
    width: 100%; border: none; background: transparent;
    color: var(--text-main); font-size: 13px;
    font-family: var(--font-body); font-weight: 600;
    outline: none; cursor: pointer; appearance: none;
  }
  .select-label {
    font-size: 12px; font-weight: 700; color: var(--text-main);
    margin-bottom: 8px; display: block;
  }
  .select-label-group {
    border: 1.5px solid var(--border-light); border-radius: 12px;
    padding: 14px 16px; margin-bottom: 10px;
    background: var(--bg-surface); transition: all 0.2s ease;
  }
  .select-label-group:hover, .select-label-group:focus-within {
    border-color: var(--brand-primary);
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.08);
  }
  .select-label-group.active {
    border-color: var(--brand-primary);
    background: var(--brand-secondary);
  }
  .select-label-group.active .select-label { color: var(--brand-primary); }
`;

const SECTIONS = [
  { id: 'appearance',    label: 'Appearance',     icon: Sun },
  { id: 'notifications', label: 'Notifications',  icon: Bell },
  { id: 'privacy',       label: 'Privacy & Data', icon: Shield },
  { id: 'language',      label: 'Language',       icon: Globe },
  { id: 'billing',       label: 'Billing',        icon: CreditCard },
  { id: 'security',      label: 'Security',       icon: Lock },
  { id: 'danger',        label: 'Danger Zone',    icon: AlertTriangle },
];

export default function AppSettings() {
  const { currentUser, userData, refreshUserData, logout } = useAuth();
  const [activeSection, setActiveSection] = useState('appearance');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeSetting, setActiveSetting] = useState(null);

  const [settings, setSettings] = useState({
    theme: 'system',
    compactMode: false,
    soundEffects: true,
    notifications: {
      contentAlerts:   true,
      aiSuggestions:   true,
      campaignUpdates: true,
      billingAlerts:   true,
      securityAlerts:  true,
      weeklyDigest:    true,
    },
    privacy: {
      profileVisible:   true,
      analyticsSharing: false,
      dataRetention:    '12months',
    },
    language:   'en',
    timezone:   'America/New_York',
    dateFormat: 'MM/DD/YYYY',
  });

  const [billing, setBilling] = useState({
    plan:          'freemium',
    planLabel:     'Free',
    creditsUsed:   0,
    creditsTotal:  100,
    nextBilling:   null,
    paymentMethod: null,
  });

  // ✅ Loads settings subdocument configuration from profile endpoint
  useEffect(() => {
    const loadSettings = async () => {
      try {
        if (!currentUser?.uid) { setLoading(false); return; }

        const response = await api.get('/users/profile');

        if (response && response.success) {
          // Backend returns profile flat on response, not nested under response.data
          const user = (response.data && response.data.subscriptionPlan)
            ? response.data
            : response;

          // Merge saved settings subdoc over defaults
          if (user.settings) {
            setSettings(prev => ({
              ...prev,
              ...user.settings,
              notifications: { ...prev.notifications, ...(user.settings.notifications || {}) },
              privacy:       { ...prev.privacy,       ...(user.settings.privacy       || {}) },
            }));
          }

          // Populate billing from user plan data
          const planLabels = {
            freemium:  'Free',
            starter:   'Starter — $14.99/mo',
            creator:   'Creator — $14.99/mo',
            growth:    'Growth — $49.99/mo',
            scale:     'Scale — $149.99/mo',
            business:  'Business — $149.99/mo',
            executive: 'Executive',
          };

          setBilling({
            plan:         user.subscriptionPlan || 'freemium',
            planLabel:    planLabels[user.subscriptionPlan] || user.subscriptionPlan,
            creditsUsed:  user.monthlyUsage?.monthlyGenerativeCreditsUsed  || 0,
            creditsTotal: user.planLimits?.monthlyGenerativeCredits        || 100,
            nextBilling:  null,
            paymentMethod: null,
          });
        }
      } catch (e) {
        console.warn('[AppSettings] Load failed, using defaults:', e);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, [currentUser?.uid]);

  const showLocalToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ✅ Writes modifications into settings subdocument object via profile routing tree
  const saveSettings = async (updates) => {
    setSaving(true);
    try {
      const newSettings = { ...settings, ...updates };
      setSettings(newSettings); // optimistic update

      // Wrap updates inside the `settings` key so the backend
      // merges into the settings subdocument, not the root profile
      const response = await api.put('/users/profile', { settings: newSettings });

      if (response && response.success) {
        showLocalToast('Settings saved');
        await refreshUserData();
      } else {
        showLocalToast(response?.error || 'Failed to save', 'error');
        // Revert optimistic update
        setSettings(settings);
      }
    } catch (e) {
      console.error('[AppSettings] Save error:', e);
      showLocalToast('Network error', 'error');
      setSettings(settings);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') return;
    setSaving(true);
    try {
      const response = await api.delete(`/users/${currentUser?.uid}`);
      if (response && response.success) {
        await logout();
      } else {
        showLocalToast('Delete failed', 'error');
      }
    } catch (e) {
      showLocalToast('Delete failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── SUB-COMPONENTS ──────────────────────────────────────────────────────────

  const Toggle = ({ checked, onChange, label, sub, settingId }) => {
    const isActive = activeSetting === settingId;
    return (
      <div
        className={`settings-row ${isActive ? 'active' : ''}`}
        onClick={() => { setActiveSetting(settingId); onChange(!checked); }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <p className="row-label">{label}</p>
            {sub && <p className="row-sub">{sub}</p>}
          </div>
          <button
            className={`toggle-track ${checked ? 'on' : 'off'}`}
            onClick={(e) => { e.stopPropagation(); setActiveSetting(settingId); onChange(!checked); }}
            aria-label={checked ? `${label} on` : `${label} off`}
            aria-pressed={checked}
          >
            <div className="toggle-knob" />
          </button>
        </div>
      </div>
    );
  };

  const Select = ({ label, value, onChange, options, settingId }) => {
    const isActive = activeSetting === settingId;
    return (
      <div className={`select-label-group ${isActive ? 'active' : ''}`}>
        <label className="select-label">{label}</label>
        <div className="select-wrapper">
          <select
            value={value}
            onChange={(e) => { setActiveSetting(settingId); onChange(e.target.value); }}
            onClick={() => setActiveSetting(settingId)}
          >
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const Card = ({ title, children, icon: Icon }) => (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border-light)',
      borderRadius: 16, padding: '20px 22px', marginBottom: 16,
      boxShadow: 'var(--shadow-md)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        {Icon && (
          <div style={{ width: 32, height: 32, borderRadius: 9, background: 'var(--brand-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={16} color="var(--brand-primary)" />
          </div>
        )}
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--text-main)', margin: 0 }}>{title}</h3>
      </div>
      {children}
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading settings...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 720, margin: '0 auto', fontFamily: 'var(--font-body)', position: 'relative' }}>
      <style>{TOAST_CSS}</style>

      {/* Toast */}
      {toast && (
        <div className="toast-enter" style={{
          position: 'fixed', top: 20, right: 20, zIndex: 1000,
          padding: '10px 16px', borderRadius: 10,
          background: toast.type === 'error' ? '#fee2e2' : '#dcfce7',
          color: toast.type === 'error' ? '#dc2626' : '#16a34a',
          border: `1px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
          fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6,
          boxShadow: 'var(--shadow-md)'
        }}>
          {toast.type === 'success' ? <Check size={14} /> : <AlertTriangle size={14} />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>
        Settings
      </h1>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 24px' }}>
        Manage your account preferences and app configuration
      </p>

      {/* Section Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap' }}>
        {SECTIONS.map(sec => {
          const Icon = sec.icon;
          const active = activeSection === sec.id;
          return (
            <button
              key={sec.id}
              onClick={() => setActiveSection(sec.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: 10,
                background: active ? 'var(--brand-primary)' : 'var(--bg-surface)',
                color: active ? 'white' : 'var(--text-muted)',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                boxShadow: active ? 'var(--shadow-brand)' : 'none',
                border: active ? 'none' : '1px solid var(--border-light)',
                transition: 'all 0.2s'
              }}
            >
              <Icon size={14} />
              {sec.label}
            </button>
          );
        })}
      </div>

      {/* ── APPEARANCE ────────────────────────────────────────────────── */}
      {activeSection === 'appearance' && (
        <>
          <Card title="Theme" icon={settings.theme === 'dark' ? Moon : Sun}>
            <div style={{ display: 'flex', gap: 10 }}>
              {[
                { value: 'light',  label: 'Light',  emoji: '☀️' },
                { value: 'dark',   label: 'Dark',   emoji: '🌙' },
                { value: 'system', label: 'System', emoji: '💻' },
              ].map(t => {
                const selected = settings.theme === t.value;
                return (
                  <button
                    key={t.value}
                    onClick={() => saveSettings({ theme: t.value })}
                    style={{
                      flex: 1, padding: '12px 8px', borderRadius: 12, cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${selected ? 'var(--brand-primary)' : 'var(--border-light)'}`,
                      background: selected ? 'var(--brand-secondary)' : 'var(--bg-surface)',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ fontSize: 20, marginBottom: 4 }}>{t.emoji}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: selected ? 'var(--brand-primary)' : 'var(--text-main)', textTransform: 'capitalize' }}>{t.label}</div>
                  </button>
                );
              })}
            </div>
          </Card>
          <Card title="Interface" icon={Smartphone}>
            <Toggle checked={settings.compactMode}  onChange={(v) => saveSettings({ compactMode: v })}  label="Compact Mode"   sub="Reduce padding and spacing for denser layouts"  settingId="compactMode" />
            <Toggle checked={settings.soundEffects} onChange={(v) => saveSettings({ soundEffects: v })} label="Sound Effects"  sub="Play subtle sounds on interactions"             settingId="soundEffects" />
          </Card>
        </>
      )}

      {/* ── NOTIFICATIONS ─────────────────────────────────────────────── */}
      {activeSection === 'notifications' && (
        <Card title="Notification Preferences" icon={Bell}>
          {Object.entries(settings.notifications).map(([key, val]) => {
            const labels = { contentAlerts: 'Content Alerts', aiSuggestions: 'AI Suggestions', campaignUpdates: 'Campaign Updates', billingAlerts: 'Billing Alerts', securityAlerts: 'Security Alerts', weeklyDigest: 'Weekly Digest' };
            const subs   = { contentAlerts: 'When your scheduled content goes live', aiSuggestions: 'New AI-generated content recommendations', campaignUpdates: 'Campaign performance milestones', billingAlerts: 'Payment confirmations and plan changes', securityAlerts: 'Login from new devices', weeklyDigest: 'Weekly performance summary every Monday' };
            return (
              <Toggle key={key} checked={val} onChange={(v) => saveSettings({ notifications: { ...settings.notifications, [key]: v } })} label={labels[key]} sub={subs[key]} settingId={`notif_${key}`} />
            );
          })}
        </Card>
      )}

      {/* ── PRIVACY ───────────────────────────────────────────────────── */}
      {activeSection === 'privacy' && (
        <>
          <Card title="Privacy Settings" icon={Shield}>
            <Toggle checked={settings.privacy.profileVisible}   onChange={(v) => saveSettings({ privacy: { ...settings.privacy, profileVisible: v } })}   label="Public Profile"   sub="Allow others to find your creator profile"                          settingId="privacy_profileVisible" />
            <Toggle checked={settings.privacy.analyticsSharing} onChange={(v) => saveSettings({ privacy: { ...settings.privacy, analyticsSharing: v } })} label="Share Analytics" sub="Contribute anonymized data to improve AI recommendations"            settingId="privacy_analyticsSharing" />
            <Select label="Data Retention" value={settings.privacy.dataRetention} onChange={(v) => saveSettings({ privacy: { ...settings.privacy, dataRetention: v } })}
              options={[
                { value: '3months',  label: '3 months' },
                { value: '6months',  label: '6 months' },
                { value: '12months', label: '12 months' },
                { value: '24months', label: '24 months' },
                { value: 'forever',  label: 'Keep forever' },
              ]}
              settingId="privacy_dataRetention"
            />
          </Card>
          <Card title="Data Export" icon={Download}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
              Download all your data including captions, media, analytics, and account history.
            </p>
            <button
              onClick={async () => {
                try {
                  showLocalToast('Export request submitted! Check your email.');
                } catch (e) {
                  showLocalToast('Export not yet available', 'error');
                }
              }}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-light)', background: 'var(--bg-surface)', color: 'var(--text-main)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Download size={14} /> Request Export
            </button>
          </Card>
        </>
      )}

      {/* ── LANGUAGE ──────────────────────────────────────────────────── */}
      {activeSection === 'language' && (
        <Card title="Language & Region" icon={Globe}>
          <Select label="Language" value={settings.language} onChange={(v) => saveSettings({ language: v })}
            options={[ { value: 'en', label: 'English' }, { value: 'es', label: 'Español' }, { value: 'fr', label: 'Français' }, { value: 'de', label: 'Deutsch' }, { value: 'pt', label: 'Português' }, { value: 'ja', label: '日本語' } ]}
            settingId="language"
          />
          <Select label="Timezone" value={settings.timezone} onChange={(v) => saveSettings({ timezone: v })}
            options={[ { value: 'America/New_York', label: 'Eastern Time (ET)' }, { value: 'America/Chicago', label: 'Central Time (CT)' }, { value: 'America/Denver', label: 'Mountain Time (MT)' }, { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' }, { value: 'Europe/London', label: 'London (GMT)' }, { value: 'Europe/Paris', label: 'Paris (CET)' }, { value: 'Asia/Tokyo', label: 'Tokyo (JST)' }, { value: 'Australia/Sydney', label: 'Sydney (AEST)' } ]}
            settingId="timezone"
          />
          <Select label="Date Format" value={settings.dateFormat} onChange={(v) => saveSettings({ dateFormat: v })}
            options={[ { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' }, { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' }, { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' } ]}
            settingId="dateFormat"
          />
        </Card>
      )}

      {/* ── BILLING ───────────────────────────────────────────────────── */}
      {activeSection === 'billing' && (
        <>
          <Card title="Current Plan" icon={CreditCard}>
            <div style={{ display: 'flex', alignItems: 'center', justifycontent: 'space-between', padding: '14px 16px', borderRadius: 12, background: 'var(--brand-secondary)', border: '1px solid var(--brand-border)', marginBottom: 16 }}>
              <div>
                <p style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 2px', textTransform: 'capitalize' }}>{billing.planLabel || billing.plan}</p>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{billing.nextBilling ? `Renews ${new Date(billing.nextBilling).toLocaleDateString()}` : 'No active subscription'}</p>
              </div>
              <button onClick={() => window.open('/dashboard/pricing', '_self')} style={{ padding: '7px 14px', borderRadius: 8, background: 'var(--brand-primary)', color: 'var(--brand-primary-contrast, white)', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                {billing.plan === 'freemium' ? 'Upgrade' : 'Change Plan'}
              </button>
            </div>
            <div>
              <div style={{ display: 'flex', justifycontent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-main)' }}>Generative Credits</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{billing.creditsUsed} / {billing.creditsTotal}</span>
              </div>
              <div style={{ height: 6, background: 'var(--border-light)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min((billing.creditsUsed / billing.creditsTotal) * 100, 100)}%`, height: '100%', borderRadius: 3, background: billing.creditsUsed / billing.creditsTotal > 0.9 ? '#ef4444' : 'var(--brand-primary)', transition: 'width 0.5s cubic-bezier(0.16,1,0.3,1)' }} />
              </div>
              <p style={{ fontSize: 10, color: 'var(--text-muted)', margin: '6px 0 0' }}>Resets on the 1st of each month</p>
            </div>
          </Card>
          <Card title="Payment Method" icon={CreditCard}>
            {billing.paymentMethod ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, background: 'var(--bg-surface)', border: '1px solid var(--border-light)' }}>
                <div style={{ width: 36, height: 24, borderRadius: 4, background: '#1a1f71', display: 'flex', alignItems: 'center', justifycontent: 'center', color: 'white', fontSize: 9, fontWeight: 800 }}>VISA</div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-main)', margin: 0 }}>•••• {billing.paymentMethod.last4}</p>
                  <p style={{ fontSize: 10, color: 'var(--text-muted)', margin: 0 }}>Expires {billing.paymentMethod.expMonth}/{billing.paymentMethod.expYear}</p>
                </div>
                <button style={{ fontSize: 11, color: 'var(--brand-primary)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}>Update</button>
              </div>
            ) : (
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>No payment method on file</p>
            )}
          </Card>
        </>
      )}

      {/* ── SECURITY ──────────────────────────────────────────────────── */}
      {activeSection === 'security' && (
        <>
          <Card title="Authentication" icon={KeyRound}>
            <div style={{ padding: '14px 0', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifycontent: 'space-between' }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 2px' }}>Two-Factor Authentication</p>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>Add an extra layer of security</p>
              </div>
              <button style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-light)', background: 'var(--bg-surface)', color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, cursor: 'not-allowed', opacity: 0.6 }}>
                Coming Soon
              </button>
            </div>
            <div style={{ padding: '14px 0 0' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 2px' }}>Active Sessions</p>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 10px' }}>You are currently signed in on this device</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: 'var(--bg-surface)', border: '1px solid var(--border-light)' }}>
                <Smartphone size={16} color="var(--brand-primary)" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-main)', margin: 0 }}>Current Session</p>
                  <p style={{ fontSize: 10, color: 'var(--text-muted)', margin: 0 }}>{navigator.userAgent?.slice(0, 50)}...</p>
                </div>
                <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 700, background: '#dcfce7', padding: '2px 8px', borderRadius: 999 }}>Active</span>
              </div>
            </div>
          </Card>
          <Card title="Account Email" icon={Mail}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, background: 'var(--bg-surface)', border: '1px solid var(--border-light)', marginBottom: 8 }}>
              <Mail size={15} color="var(--brand-primary)" />
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)', margin: 0 }}>
                {currentUser?.email || 'No email on file'}
              </p>
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>This is the email address associated with your Infleuince account.</p>
          </Card>
          <Card title="Password" icon={Lock}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
              Your password is managed through Firebase Authentication. Use the button below to receive a reset link.
            </p>
            <button
              onClick={() => showLocalToast('Reset link sent to your email')}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-light)', background: 'var(--bg-surface)', color: 'var(--text-main)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Mail size={14} /> Send Reset Link
            </button>
          </Card>
        </>
      )}

      {/* ── DANGER ZONE ───────────────────────────────────────────────── */}
      {activeSection === 'danger' && (
        <Card title="Danger Zone" icon={AlertTriangle}>
          <div style={{ padding: '14px 16px', borderRadius: 12, background: '#fef2f2', border: '1px solid #fecaca', marginBottom: 12 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#dc2626', margin: '0 0 4px' }}>Delete Account</p>
            <p style={{ fontSize: 11, color: '#991b1b', margin: '0 0 12px', lineHeight: 1.5 }}>
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            {!showDeleteModal ? (
              <button onClick={() => setShowDeleteModal(true)} style={{ padding: '8px 16px', borderRadius: 8, background: '#dc2626', color: 'white', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Trash2 size={14} /> Delete Account
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ fontSize: 11, color: '#991b1b', margin: 0, fontWeight: 600 }}>Type "DELETE" to confirm:</p>
                <input type="text" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="DELETE" style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #fecaca', background: 'white', color: '#dc2626', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-body)' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setShowDeleteModal(false); setDeleteConfirm(''); }} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-light)', background: 'white', color: 'var(--text-main)', fontSize: 12, fontWeight: 700, cursor: 'pointer', flex: 1 }}>Cancel</button>
                  <button onClick={handleDeleteAccount} disabled={deleteConfirm !== 'DELETE' || saving} style={{ padding: '8px 16px', borderRadius: 8, background: deleteConfirm === 'DELETE' ? '#dc2626' : '#fca5a5', color: 'white', border: 'none', fontSize: 12, fontWeight: 700, cursor: deleteConfirm === 'DELETE' ? 'pointer' : 'not-allowed', flex: 1, display: 'flex', alignItems: 'center', justifycontent: 'center', gap: 6 }}>
                    {saving ? <Loader2 size={14} /> : <Trash2 size={14} />} Permanently Delete
                  </button>
                </div>
              </div>
            )}
          </div>
          <div style={{ padding: '14px 16px', borderRadius: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-light)' }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)', margin: '0 0 4px' }}>Sign Out</p>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 12px' }}>Sign out from all devices.</p>
            <button onClick={logout} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-light)', background: 'var(--bg-surface)', color: 'var(--text-main)', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <LogOut size={14} /> Sign Out Everywhere
            </button>
          </div>
        </Card>
      )}

      {/* Saving indicator */}
      {saving && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, background: 'var(--bg-surface)', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-md)', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>
          <Loader2 size={14} /> Saving...
        </div>
      )}
    </div>
  );
}